import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";

export const useFetchTeamGroup = () => {
  const { fetchData } = useApi();
  const { organizationId, sport, clubId } = useParams();

  return useQuery({
    queryKey: ["teamGroups", organizationId, clubId],
    queryFn: async () => {
      const result = await fetchData({
        url: `${sport}/o/${organizationId}/entityGroups/${clubId}`,
      });
      return result?.data?.[0];
    },
    enabled: !!clubId,
  });
};

export const useFetchTeamGroups = (params = {}, enabled) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/entityGroups`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["teamGroups", organizationId, JSON.stringify(params)],
    queryFn: async () => {
      const result = await fetchData({
        url: URL,
      });
      return result?.data || [];
    },
    enabled: !!enabled,
  });
};

export const useCreateTeamGroup = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation({
    mutationFn: async (data) => {
      const result = await mutateData({
        url: `${sport}/o/${organizationId}/entityGroups`,
        method: "POST",
        data,
      });

      return result?.data?.[0];
    },
    onSuccess: async (createdEntityGroup) => {
      if (!createdEntityGroup?.entityGroupId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        if (updatedScope.resources.entityGroups) {
          updatedScope.resources.entityGroups[createdEntityGroup.entityGroupId] = createdEntityGroup;
        } else {
          updatedScope.resources.entityGroups = {
            [createdEntityGroup.entityGroupId]: createdEntityGroup,
          };
        }

        updatedScope.scope.entityGroupTree.push({
          entityGroupId: createdEntityGroup.entityGroupId,
          entityIds: [],
        });
      });

      notification.success({
        message: formatMessage({
          id: "club.create.success",
        }),
        placement: "bottomRight",
      });

      await Promise.all([invalidateCache(), queryClient.invalidateQueries({ queryKey: ["teamGroups", organizationId] })]);
    },
  });
};

export const useUpdateTeamGroup = () => {
  const { sport, organizationId, clubId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation({
    mutationFn: async (data) => {
      const result = await mutateData({
        url: `${sport}/o/${organizationId}/entityGroups/${clubId}`,
        method: "PUT",
        data,
      });

      return result?.data?.[0];
    },
    onSuccess: async (updatedEntityGroup) => {
      if (!updatedEntityGroup?.entityGroupId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        const existingEntityGroup = updatedScope.resources?.entityGroups?.[clubId];

        if (existingEntityGroup && updatedEntityGroup) {
          updatedScope.resources.entityGroups[clubId] = {
            ...existingEntityGroup,
            ...updatedEntityGroup,
            entityGroupId: clubId,
          };
        }
      });

      notification.success({
        message: formatMessage({
          id: "club.update.success",
        }),
        placement: "bottomRight",
      });

      await Promise.all([invalidateCache(), queryClient.invalidateQueries({ queryKey: ["teamGroups", organizationId, clubId] })]);
    },
  });
};
